<template>
<div>
  <div v-if="loading" class="text-center my-3">
    <b-spinner label="Loading..."></b-spinner> Calculating Directional Business Case...
  </div>

  <b-alert show variant="info" class="py-2" style="font-size:13px">This TCO/Business Case functionality is a work-in-progress. Feedback is welcome!</b-alert>
  <p style="font-size:13px">Use this dialog to help understand the potential overall financials beyond just the projected cost of the AWS database services themselves.  You can use this dialog to see an Instant Business Case or to jump to the Quick Directional Business Case (DBC) Calculator or launch the home page for the Full DBC tool.  You can also use this dialog to download a JSON file that you can then upload to an Incentives Calculator to project potential partner and service financial incentives that can be used.</p>

  <!-- Next Steps -->
  <b-card class="mb-3" no-body>
    <b-card-header v-b-toggle.next-steps style="cursor:pointer" class="py-2">
      Next Steps {{ showIncentives ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="next-steps" v-model="showIncentives">
      <b-card-body>
        <ol>
          <li>View an Instant Business Case (see section below)</li>
          <li><a href="https://sample-solutions.com/" target="_blank">Go to the DBC Quick Calculator &#x2197;</a></li>
          <li><a href="https://aws.highspot.com/items/65e8a7883c22e0041c4487ca" target="_blank">Go to the Full DBC home page &#x2197;</a></li>
          <li>
            <a href="https://mapincentivecalculator.harmony.a2z.com/" target="_blank">Go to the AWS Incentives Calculator &#x2197;</a>
            <br/><span style="font-size:13px">Download an AWS Pricing Calculator JSON file to upload to the AWS Incentives Calculator.  NOTE: be sure that your source workload is MAP-eligible (runs on-premises or in another cloud) as that is what the Incentive Calculator will be assuming when you upload the JSON.</span>
            <br/>
            <span v-for="si in activeScenarios" :key="'dl-'+si">
              <b-button size="sm" variant="outline-primary" @click="downloadPricingJSON(si, 'rds')" class="mr-2 mb-1 mt-1">Download AWS Pricing Calculator JSON for RDS-Oracle{{ multiScenario ? ' (' + totals[si].scenarioName + ')' : '' }}</b-button>
              <b-button v-if="scenarioHasAurora(si)" size="sm" variant="outline-primary" @click="downloadPricingJSON(si, 'aurora')" class="mr-2 mb-1 mt-1">Download AWS Pricing Calculator JSON for Aurora{{ multiScenario ? ' (' + totals[si].scenarioName + ')' : '' }}</b-button>
            </span>
          </li>
          <li>Review the <a href="https://aws.highspot.com/items/69eb95db9f1db54fb5b9ceb8#1" target="_blank">WASO Enablement slides &#x2197;</a> in Highspot</li>
          <li>Learn more at the <a href="https://aws.highspot.com/items/699508190a4d02cff79732b5?lfrm=irel.0" target="_blank">WASO Highspot Landing Page &#x2197;</a></li>
          <li>Chat in Slack with the <a href="https://amazon.enterprise.slack.com/archives/D0B1R33PRBJ" target="_blank">@DBS Pricing Assistant &#x2197;</a> application</li>
        </ol>
      </b-card-body>
    </b-collapse>
  </b-card>

  <!-- Instant Business Case -->
  <b-card class="mb-3" no-body>
    <b-card-header v-b-toggle.instant-dbc style="cursor:pointer" class="py-2">
      Instant Business Case {{ showDBC ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="instant-dbc" v-model="showDBC">
      <b-card-body>

  <p style="font-size:13px">The Instant Business Case (DBC) calculates a <b>5-year Total Cost of Ownership (TCO)</b> comparison between our assumptions about your source on-premises environment and one or more AWS target configurations. All costs are projected over 5 years.  For more control over the business case, consider using the Quick DBC or Full DBC.</p>

  <div v-if="error">
    <b-alert show variant="danger">{{ error }}</b-alert>
  </div>

  <div v-if="activeScenarios.some(i => totals[i].sourceNonX86)">
    <b-alert show variant="danger">CAUTION: One or more of the source environments appear to be non-x86 platforms.  This business case tool assumes x86 source platforms when estimating Oracle Support fees.  Therefore, the estimated Oracle Support fees for the source environment are likely wrong.</b-alert>
  </div>

  <div v-if="sourcesDiffer">
    <b-alert show variant="warning">NOTE: Source environment totals differ across scenarios (e.g. due to PDB/CDB or duplicate exclusion rules).  The Source column below reflects scenario "{{ totals[activeScenarios[0]].scenarioName }}".</b-alert>
  </div>

  <div v-if="activeScenarios.some(i => totals[i].totalUnshapedRDS > 0)">
    <b-alert show variant="danger">WARNING: One or more scenarios have databases that were not sized for RDS-Oracle and are not included in the RDS totals (but are included in the source totals) which makes the source to target comparison likely invalid.</b-alert>
  </div>

  <div v-if="activeScenarios.some(i => totals[i].totalUnshapedAurora > 0)">
    <b-alert show variant="danger">WARNING: One or more scenarios have databases that were not sized for Aurora PostgreSQL and are not included in the Aurora totals (but are included in the source totals) which makes the source to target comparison likely invalid.</b-alert>
  </div>

  <div v-if="activeScenarios.some(i => totals[i].totalUnshapedADBS > 0)">
    <b-alert show variant="danger">WARNING: One or more scenarios have databases that were not sized for Autonomous Database and are not included in the ADB-S totals (but are included in the source totals) which makes the source to target comparison likely invalid.</b-alert>
  </div>
  
  <b-card class="mb-3" no-body v-if="!loading">
    <b-card-header v-b-toggle.business-benefits style="cursor:pointer" class="py-2">
      Business Benefits - User Input {{ showBenefits ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="business-benefits" v-model="showBenefits">
      <b-card-body>
        <b>Manage Less to Innovate More!</b><br/>
        <span style="font-size:14px">An <a target="_blank" href="https://d1.awsstatic.com/product-marketing/RDS/IDC%20Business%20Value%20of%20Amazon%20RDS%20Whitepaper%202024.pdf">IDC study</a> found that interviewed organizations achieved significant value from using Amazon Relational Database Service (Amazon RDS) to cost effectively set up, operate, and scale relational databases in the cloud. Through reduced costs, greater staff and database performance, and business enablement, IDC calculated that interviewed organizations achieved an average annual benefit of $11.7 million per year, resulting in a three-year ROI of 258%.
        <br/><br/>
        </span>
        <b-form-group label="Managed Database Business Benefit Annual Amount ($)" label-cols-sm="4" label-size="sm" description="Enter a placeholder annual amount for the business benefits achieved by managed databases.  The IDC study reported an average benefit of $19,860 per database per year.  This field defaults to a conservative $5,000 per year per database, but feel free to change as desired.  This amount is multiplied by 5 for the 5-year TCO.">
                <b-form-input v-model.number="adv.business_benefit_amount" type="number" size="sm"></b-form-input>
                
        </b-form-group>
        <b-button  variant="primary" @click="fetchDBC()" :disabled="loading">Recalculate</b-button>
      </b-card-body>
    </b-collapse>
  </b-card>
  
  
  <!-- Incentives - User Input -->
  <b-card class="mb-3" no-body v-if="!loading">
    <b-card-header v-b-toggle.incentives-input style="cursor:pointer" class="py-2">
      Incentives - User Input {{ showIncentivesInput ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="incentives-input" v-model="showIncentivesInput">
      <b-card-body>
        <span style="font-size:13px">You can use the <a href="https://mapincentivecalculator.harmony.a2z.com/" target="_blank">AWS Incentives Calculator</a> to estimate these values.  For the Partner Cash incentives, you can use your judgement on what items (if any) you want to include as benefits-- you might choose to include the 'Managed Database Accelerator (MDA)' incentive but not the 'Assess' incentive for example.</span>
        <br/><br/>
        <b-form-group label="Year1 Service Credits ($)" label-cols-sm="4" label-size="sm">
          <b-form-input v-model="adv.year1_service_credits" type="text" size="sm" @blur="adv.year1_service_credits = parseCurrency(adv.year1_service_credits)"></b-form-input>
        </b-form-group>
        <b-form-group label="Year1 Partner Cash Incentives ($)" label-cols-sm="4" label-size="sm">
          <b-form-input v-model="adv.year1_partner_cash_incentives" type="text" size="sm" @blur="adv.year1_partner_cash_incentives = parseCurrency(adv.year1_partner_cash_incentives)"></b-form-input>
        </b-form-group>
        <b-form-group label="AWS Cross-Service Private Discount %" label-cols-sm="4" label-size="sm" description="Enter a cross-service PPA/EDP discount.  Note: if you want to enter a service-specific discount (which does not stack), be sure to use the DBCSI auto-right sizer's AWS Pricing Preference option (available under More Preferences/Controls) to use only On-Demand pricing because service-specific discounts do not apply to Reserved Instance or Database Savings Plans pricing.">
          <b-form-input v-model.number="adv.discount_percentage" type="number" step="1" size="sm" @change="fetchDBC()"></b-form-input>
        </b-form-group>
        <b-button size="sm" variant="primary" @click="fetchDBC()" :disabled="loading">Recalculate</b-button>
      </b-card-body>
    </b-collapse>
  </b-card>

  <!-- Advanced Settings (hidden) -->
  <b-card class="mb-3" no-body v-if="false">
    <b-card-header v-b-toggle.advanced-settings style="cursor:pointer" class="py-2">
      Advanced Settings {{ showAdvanced ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="advanced-settings" v-model="showAdvanced">
      <b-card-body>
        <b-form-group label="Source Total VCPUs" label-cols-sm="4" label-size="sm">
          <b-form-input v-model.number="adv.total_vcpus" type="number" size="sm"></b-form-input>
        </b-form-group>
        <b-form-group label="Annual Growth %" label-cols-sm="4" label-size="sm">
          <b-form-input v-model.number="adv.annual_growth_percentage" type="number" step="0.1" size="sm"></b-form-input>
        </b-form-group>
        
        <b-button size="sm" variant="primary" @click="fetchDBC()" :disabled="loading">Recalculate</b-button>
      </b-card-body>
    </b-collapse>
  </b-card>

  <!-- Source Environment Assumptions -->
  <b-card class="mb-3" no-body v-if="!loading">
    <b-card-header v-b-toggle.source-assumptions style="cursor:pointer" class="py-2">
      Source Environment Assumptions {{ showSourceAssumptions ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="source-assumptions" v-model="showSourceAssumptions">
      <b-card-body style="font-size:13px">
        <p>
          <b>Databases:</b> {{ totals[activeScenarios[0]].totalSourceDBCount }} &nbsp;
          <b>Instances:</b> {{ totals[activeScenarios[0]].totalSourceInstanceCount }} &nbsp;
          <b>Distinct Hostnames:</b> {{ totals[activeScenarios[0]].totalSourceDistinctServerCount }}
        </p>
        <p>
          <b>Total Apparent VCPUs:</b> {{ totals[activeScenarios[0]].totalSourceVCPU }} &nbsp;
          <b>Total Distinct VCPUs:</b> {{ totals[activeScenarios[0]].totalSourceDistinctServerVCPUs }} &nbsp;
          <b>Total Distinct Cores:</b> {{ totals[activeScenarios[0]].totalSourceDistinctServerCores }}
        </p>
        <p><b>Usable Disk Space:</b> {{ totals[activeScenarios[0]].totalSourceUsableDiskSpace.toLocaleString('en-US') }} GB</p>
        <p v-if="totals[activeScenarios[0]].sourceExadata"><b>Exadata:</b> Yes</p>
        <p v-if="totals[activeScenarios[0]].sourceWarning" class="text-warning">{{ totals[activeScenarios[0]].sourceWarning }}</p>
        <div v-if="totals[activeScenarios[0]].sourceDistinctServers && totals[activeScenarios[0]].sourceDistinctServers.length > 0">
          <b>Distinct Servers:</b>
          <b-table-simple small bordered class="mt-1">
            <b-thead><b-tr>
              <b-th>Hostname</b-th><b-th class="text-right">Cores</b-th><b-th class="text-right">VCPUs</b-th><b-th class="text-right">Memory</b-th><b-th>Platform</b-th>
            </b-tr></b-thead>
            <b-tbody>
              <b-tr v-for="srv in totals[activeScenarios[0]].sourceDistinctServers" :key="srv.hostname">
                <b-td>{{ srv.hostname }}</b-td><b-td class="text-right">{{ srv.cores }}</b-td><b-td class="text-right">{{ srv.vcpus }}</b-td><b-td class="text-right">{{ srv.memory }}</b-td><b-td>{{ srv.platform }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>
      </b-card-body>
    </b-collapse>
  </b-card>

  <div v-if="!loading && !error && sourceResult && targetColumns.length > 0">

    <!-- Combined 5-Year TCO Comparison Table -->
    <b-card class="mb-3" no-body>
      <b-table-simple small bordered responsive ref="dbcTable">
        <b-thead>
          <b-tr>
            <b-th>5-Year TCO</b-th>
            <b-th class="text-right" variant="info">Source (commodity x86)</b-th>
            <b-th class="text-right" variant="info" v-if="exadataSourceResult">Source (Exadata)</b-th>
            <b-th class="text-right" variant="success" v-for="col in targetColumns" :key="'h-'+col.key">{{ col.label }}</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>

          <!-- Compute -->
          <b-tr @click="showCompute=!showCompute" style="cursor:pointer">
            <b-td>Compute {{ showCompute ? '&#9650;' : '&#9660;' }}</b-td>
            <b-td class="text-right">$ {{ fmt(sourceResult.compute_5yr_cost) }}</b-td>
            <b-td class="text-right" v-if="exadataSourceResult">$ {{ fmt(exadataSourceResult.compute_5yr_cost) }}</b-td>
            <b-td class="text-right" v-for="col in targetColumns" :key="'comp-'+col.key">$ {{ fmt(col.result.compute_5yr_cost) }}</b-td>
          </b-tr>
          <template v-if="showCompute">
            <b-tr v-for="bk in mergedBreakoutKeys('compute_breakout')" :key="'cb-'+bk" class="bg-light">
              <b-td class="pl-4 text-muted">{{ breakoutLabel(bk) }}</b-td>
              <b-td class="text-right text-muted">{{ sourceResult.compute_breakout && sourceResult.compute_breakout[bk] != null ? fmtVal(sourceResult.compute_breakout[bk], bk) : '' }}</b-td>
              <b-td class="text-right text-muted" v-if="exadataSourceResult">{{ exadataSourceResult.compute_breakout && exadataSourceResult.compute_breakout[bk] != null ? fmtVal(exadataSourceResult.compute_breakout[bk], bk) : '' }}</b-td>
              <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'cb-'+col.key+'-'+bk">{{ col.result.compute_breakout && col.result.compute_breakout[bk] != null ? fmtVal(col.result.compute_breakout[bk], bk) : '' }}</b-td>
            </b-tr>
          </template>

          <!-- Storage -->
          <b-tr @click="showStorage=!showStorage" style="cursor:pointer">
            <b-td>Storage {{ showStorage ? '&#9650;' : '&#9660;' }}</b-td>
            <b-td class="text-right">$ {{ fmt(sourceResult.storage_5yr_cost) }}</b-td>
            <b-td class="text-right" v-if="exadataSourceResult">$ {{ fmt(exadataSourceResult.storage_5yr_cost) }}</b-td>
            <b-td class="text-right" v-for="col in targetColumns" :key="'stor-'+col.key">$ {{ fmt(col.result.storage_5yr_cost) }}</b-td>
          </b-tr>
          <template v-if="showStorage">
            <b-tr v-for="bk in mergedBreakoutKeys('storage_breakout')" :key="'sb-'+bk" class="bg-light">
              <b-td class="pl-4 text-muted">{{ breakoutLabel(bk) }}</b-td>
              <b-td class="text-right text-muted">{{ sourceResult.storage_breakout && sourceResult.storage_breakout[bk] != null ? fmtVal(sourceResult.storage_breakout[bk], bk) : '' }}</b-td>
              <b-td class="text-right text-muted" v-if="exadataSourceResult">{{ exadataSourceResult.storage_breakout && exadataSourceResult.storage_breakout[bk] != null ? fmtVal(exadataSourceResult.storage_breakout[bk], bk) : '' }}</b-td>
              <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'sb-'+col.key+'-'+bk">{{ col.result.storage_breakout && col.result.storage_breakout[bk] != null ? fmtVal(col.result.storage_breakout[bk], bk) : '' }}</b-td>
            </b-tr>
          </template>

          <!-- Data Center and Networking -->
          <template v-if="sourceResult.datacenter_network_5yr_cost || (exadataSourceResult && exadataSourceResult.datacenter_network_5yr_cost) || targetColumns.some(c => c.result.datacenter_network_5yr_cost)">
            <b-tr @click="showDatacenterNetwork=!showDatacenterNetwork" style="cursor:pointer">
              <b-td>Data Center and Networking {{ showDatacenterNetwork ? '&#9650;' : '&#9660;' }}</b-td>
              <b-td class="text-right">{{ sourceResult.datacenter_network_5yr_cost ? '$ ' + fmt(sourceResult.datacenter_network_5yr_cost) : '' }}</b-td>
              <b-td class="text-right" v-if="exadataSourceResult">{{ exadataSourceResult.datacenter_network_5yr_cost ? '$ ' + fmt(exadataSourceResult.datacenter_network_5yr_cost) : '' }}</b-td>
              <b-td class="text-right" v-for="col in targetColumns" :key="'dcn-'+col.key">{{ col.result.datacenter_network_5yr_cost ? '$ ' + fmt(col.result.datacenter_network_5yr_cost) : '' }}</b-td>
            </b-tr>
            <template v-if="showDatacenterNetwork">
              <b-tr v-for="bk in mergedBreakoutKeys('datacenter_network_breakout')" :key="'dcnb-'+bk" class="bg-light">
                <b-td class="pl-4 text-muted">{{ breakoutLabel(bk) }}</b-td>
                <b-td class="text-right text-muted">{{ sourceResult.datacenter_network_breakout && sourceResult.datacenter_network_breakout[bk] != null ? fmtVal(sourceResult.datacenter_network_breakout[bk], bk) : '' }}</b-td>
                <b-td class="text-right text-muted" v-if="exadataSourceResult">{{ exadataSourceResult.datacenter_network_breakout && exadataSourceResult.datacenter_network_breakout[bk] != null ? fmtVal(exadataSourceResult.datacenter_network_breakout[bk], bk) : '' }}</b-td>
                <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'dcnb-'+col.key+'-'+bk">{{ col.result.datacenter_network_breakout && col.result.datacenter_network_breakout[bk] != null ? fmtVal(col.result.datacenter_network_breakout[bk], bk) : '' }}</b-td>
              </b-tr>
            </template>
          </template>

          <!-- Database Support and Update Fees -->
          <template v-if="sourceResult.commercial_db_support_5yr_costs || (exadataSourceResult && exadataSourceResult.commercial_db_support_5yr_costs) || targetColumns.some(c => c.result.commercial_db_support_5yr_cost)">
            <b-tr @click="showLicense=!showLicense" style="cursor:pointer">
              <b-td>Database Support and Update Fees {{ showLicense ? '&#9650;' : '&#9660;' }}</b-td>
              <b-td class="text-right">{{ sourceResult.commercial_db_support_5yr_costs ? '$ ' + fmt(sourceResult.commercial_db_support_5yr_costs) : '' }}</b-td>
              <b-td class="text-right" v-if="exadataSourceResult">{{ exadataSourceResult.commercial_db_support_5yr_costs ? '$ ' + fmt(exadataSourceResult.commercial_db_support_5yr_costs) : '' }}</b-td>
              <b-td class="text-right" v-for="col in targetColumns" :key="'lic-'+col.key">{{ col.result.commercial_db_support_5yr_cost ? '$ ' + fmt(col.result.commercial_db_support_5yr_cost) : '' }}</b-td>
            </b-tr>
            <template v-if="showLicense">
              <b-tr v-for="bk in mergedBreakoutKeys('license_breakout')" :key="'lb-'+bk" class="bg-light">
                <b-td class="pl-4 text-muted">{{ breakoutLabel(bk) }}</b-td>
                <b-td class="text-right text-muted">{{ sourceResult.license_breakout && sourceResult.license_breakout[bk] != null ? fmtVal(sourceResult.license_breakout[bk], bk) : '' }}</b-td>
                <b-td class="text-right text-muted" v-if="exadataSourceResult">{{ exadataSourceResult.license_breakout && exadataSourceResult.license_breakout[bk] != null ? fmtVal(exadataSourceResult.license_breakout[bk], bk) : '' }}</b-td>
                <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'lb-'+col.key+'-'+bk">{{ col.result.license_breakout && col.result.license_breakout[bk] != null ? fmtVal(col.result.license_breakout[bk], bk) : '' }}</b-td>
              </b-tr>
            </template>
          </template>

          <!-- Database Labor -->
          <template v-if="sourceResult.database_labor_5yr_cost || (exadataSourceResult && exadataSourceResult.database_labor_5yr_cost) || targetColumns.some(c => c.result.database_labor_5yr_cost)">
            <b-tr @click="showLabor=!showLabor" style="cursor:pointer">
              <b-td>Database Labor {{ showLabor ? '&#9650;' : '&#9660;' }}</b-td>
              <b-td class="text-right">{{ sourceResult.database_labor_5yr_cost ? '$ ' + fmt(sourceResult.database_labor_5yr_cost) : '' }}</b-td>
              <b-td class="text-right" v-if="exadataSourceResult">{{ exadataSourceResult.database_labor_5yr_cost ? '$ ' + fmt(exadataSourceResult.database_labor_5yr_cost) : '' }}</b-td>
              <b-td class="text-right" v-for="col in targetColumns" :key="'lab-'+col.key">{{ col.result.database_labor_5yr_cost ? '$ ' + fmt(col.result.database_labor_5yr_cost) : '' }}</b-td>
            </b-tr>
            <template v-if="showLabor">
              <b-tr v-for="bk in mergedBreakoutKeys('labor_breakout')" :key="'labr-'+bk" class="bg-light">
                <b-td class="pl-4 text-muted">{{ breakoutLabel(bk) }}</b-td>
                <b-td class="text-right text-muted">{{ sourceResult.labor_breakout && sourceResult.labor_breakout[bk] != null ? fmtVal(sourceResult.labor_breakout[bk], bk) : '' }}</b-td>
                <b-td class="text-right text-muted" v-if="exadataSourceResult">{{ exadataSourceResult.labor_breakout && exadataSourceResult.labor_breakout[bk] != null ? fmtVal(exadataSourceResult.labor_breakout[bk], bk) : '' }}</b-td>
                <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'labr-'+col.key+'-'+bk">{{ col.result.labor_breakout && col.result.labor_breakout[bk] != null ? fmtVal(col.result.labor_breakout[bk], bk) : '' }}</b-td>
              </b-tr>
            </template>
          </template>

          <!-- Business Benefits (targets only) -->
          <template v-if="targetColumns.some(c => c.result.business_benefits_5yr)">
            <b-tr>
              <b-td>Managed Database Business Benefits</b-td>
              <b-td class="text-right"></b-td>
              <b-td class="text-right" v-if="exadataSourceResult"></b-td>
              <b-td class="text-right" v-for="col in targetColumns" :key="'bb-'+col.key">{{ col.result.business_benefits_5yr ? '$ ' + fmt(col.result.business_benefits_5yr * -1) : '' }}</b-td>
            </b-tr>
          </template>

          <!-- Incentives (RDS/Aurora/Consolidated only) -->
          <template v-if="hasIncentives">
            <b-tr @click="showIncentivesRow=!showIncentivesRow" style="cursor:pointer">
              <b-td>Incentives {{ showIncentivesRow ? '&#9650;' : '&#9660;' }}</b-td>
              <b-td class="text-right"></b-td>
              <b-td class="text-right" v-if="exadataSourceResult"></b-td>
              <b-td class="text-right" v-for="col in targetColumns" :key="'inc-'+col.key">{{ colIncentives(col) ? '$ ' + fmt(colIncentives(col) * -1) : '' }}</b-td>
            </b-tr>
            <template v-if="showIncentivesRow">
              <b-tr class="bg-light">
                <b-td class="pl-4 text-muted">Service Credits (Year 1)</b-td>
                <b-td class="text-right text-muted"></b-td>
                <b-td class="text-right text-muted" v-if="exadataSourceResult"></b-td>
                <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'inc-sc-'+col.key">{{ colIsIncentiveEligible(col) && adv.year1_service_credits ? '$ ' + fmt(adv.year1_service_credits * -1) : '' }}</b-td>
              </b-tr>
              <b-tr class="bg-light">
                <b-td class="pl-4 text-muted">Partner Cash Incentives (Year 1)</b-td>
                <b-td class="text-right text-muted"></b-td>
                <b-td class="text-right text-muted" v-if="exadataSourceResult"></b-td>
                <b-td class="text-right text-muted" v-for="col in targetColumns" :key="'inc-pc-'+col.key">{{ colIsIncentiveEligible(col) && adv.year1_partner_cash_incentives ? '$ ' + fmt(adv.year1_partner_cash_incentives * -1) : '' }}</b-td>
              </b-tr>
            </template>
          </template>

          <!-- Total Row -->
          <b-tr variant="primary">
            <b-td><b>Total 5-Year Cost</b></b-td>
            <b-td class="text-right"><b>$ {{ fmt(sourceResult.total_5yr_cost) }}</b></b-td>
            <b-td class="text-right" v-if="exadataSourceResult"><b>$ {{ fmt(exadataSourceResult.total_5yr_cost) }}</b></b-td>
            <b-td class="text-right" v-for="col in targetColumns" :key="'tot-'+col.key"><b>$ {{ fmt(colTotal(col)) }}</b></b-td>
          </b-tr>

          <!-- Savings Rows -->
          <b-tr>
            <b-td><b>{{ exadataSourceResult ? 'Estimated Savings to Source (Exadata) / (Additional Cost)' : 'Estimated Savings / (Additional Cost)' }}</b></b-td>
            <b-td class="text-right"></b-td>
            <b-td class="text-right" v-if="exadataSourceResult"></b-td>
            <b-td class="text-right" v-for="col in targetColumns" :key="'sav-'+col.key" :class="colSavings(col) >= 0 ? 'text-success' : 'text-danger'">
              <b>$ {{ fmt(Math.abs(colSavings(col))) }} [{{ colSavingsPercent(col) }}%]</b>
            </b-td>
          </b-tr>

        </b-tbody>
      </b-table-simple>
      <div class="p-2 text-right">
        <b-button size="sm" variant="outline-secondary" @click="copyTable">{{ copyLabel }}</b-button>
      </div>
    </b-card>

  </div>

  <!-- How Instant Business Case Calculations Work -->
  <b-card class="mb-3" no-body v-if="!loading && !error && sourceResult">
    <b-card-header v-b-toggle.dbc-help style="cursor:pointer" class="py-2">
      How Instant Business Case Calculations Work {{ showHelp ? '&#9650;' : '&#9660;' }}
    </b-card-header>
    <b-collapse id="dbc-help" v-model="showHelp">
      <b-card-body style="font-size:13px">

        <h6>Growth Rate Projections</h6>
        <ul>
          <li><b>General costs</b> (compute, storage, networking, DC operations): Compounded monthly at the configured <i>Annual Growth %</i> (default 10%). Each month's cost = base × (1 + annual_rate/100)^(1/12) raised to the month number, summed over 60 months.</li>
          <li><b>License support costs</b>: Compounded monthly at a separate <i>Annual Support Growth %</i> (default 4%), reflecting typical Oracle/SQL Server support escalation.</li>
          <li><b>Some costs are flat</b> (no growth applied): misc server support, storage hardware/software support, and hardware depreciation-based items that are simply multiplied by 5 years.</li>
        </ul>

        <hr/>
        <h6>Source: On-Premises (Commodity x86)</h6>
        <p>Models a standard on-premises data center using Dell R830 servers.</p>

        <b>1. Compute</b>
        <ul>
          <li><b>Servers needed</b>: ⌈total_vcpus ÷ 64⌉ (Dell R830: 2 sockets × 16 cores = 32 cores = 64 vCPUs per server)</li>
          <li><b>Base server cost</b>: servers × $11,217 per server, depreciated over 4 years, projected with annual growth</li>
          <li><b>Extra memory</b>: total_vcpus × 8 GB/vCPU × $26/GB, depreciated over 4 years, projected with annual growth</li>
          <li><b>Virtualization</b>: servers × 2 sockets × $899/socket (vSphere), projected with annual growth</li>
          <li><b>OS support</b>: servers × $799/socket-pair (RHEL), projected with annual growth</li>
          <li><b>Misc server support</b>: servers × $1,000/year × 5 years (flat, no growth)</li>
        </ul>

        <b>2. Storage</b>
        <ul>
          <li><b>Primary (SAN SSD)</b>: storage_gb × $3.00/GB × 70% discount = $2.10/GB, depreciated over 4 years × 5 years</li>
          <li><b>Backup (SAN HDD)</b>: storage_gb × $1.10/GB × 70% discount = $0.77/GB, depreciated over 4 years × 5 years</li>
          <li><b>Hardware support</b>: 15% of (primary + backup undepreciated cost) × 5 years (flat)</li>
          <li><b>Software support</b>: 20% of (primary annual depreciated cost) × 5 years (flat)</li>
        </ul>

        <b>3. Database Support and Update Fees (Oracle)</b>
        <ul>
          <li><b>Processor licenses</b>: ⌈total_vcpus ÷ 2⌉ × 0.5 core factor (x86)</li>
          <li><b>EE list price per processor</b>: $47,500 (EE) + $7,500 (Diagnostic) + $5,000 (Tuning) + $23,000 (RAC) + $11,500 (Partitioning) = $94,500</li>
          <li><b>Discounted price</b>: list × 30% (assumes typical 70% Oracle discount)</li>
          <li><b>Annual support</b>: 22% of discounted license cost, projected with support growth rate (default 4%)</li>
          <li><b>Future license growth</b>: If annual growth &gt; 0%, additional processor licenses are projected for year 5, and their license acquisition cost + support is added</li>
          <li>Note: The cost of <i>holding</i> Oracle licenses is NOT included — only the annual support/update fees</li>
        </ul>

        <b>4. Data Center and Networking</b>
        <ul>
          <li><b>Racks needed</b>: ⌈(servers × 2 RU) ÷ 28 usable RU⌉ + 1 storage rack</li>
          <li><b>Per-rack hardware</b>: $1,850 chassis + 2 × $460 PDU + 2 × ($3,500 ToR switch + 4 yr × $500 support) = $14,770/rack, depreciated over 4 years, projected with annual growth</li>
          <li><b>DC operations</b>: racks × $1,400/month, projected with annual growth</li>
          <li><b>Network</b>: 20% of rack hardware cost, depreciated over 4 years, projected with annual growth</li>
          <li><b>Network support</b>: 25% of network cost (annual), projected with annual growth</li>
        </ul>

        <b>5. Database Labor</b>
        <ul>
          <li>Based on a reference model of 7 functional roles (Architecture, DBA, Developer, Engineering, Infrastructure, Storage Admin, System Admin)</li>
          <li>Each role has annual "Operate" hours for the "OnPremises" platform type</li>
          <li>Total reference hours for OnPremises: 4,102 hours/year (for a reference system of 576 vCPUs)</li>
          <li><b>Scaling</b>: hours × (your_vcpus ÷ 576) to scale for your workload size</li>
          <li><b>Rate</b>: 100% onshore at $130/hour</li>
          <li><b>Projection</b>: 5 years with 4% annual growth on labor costs</li>
        </ul>

        <hr/>
        <h6>Source: On-Premises Exadata</h6>
        <p>Models Oracle Exadata hardware with depreciation and support costs.</p>

        <b>1. Compute (DB Servers)</b>
        <ul>
          <li>Cores per server: X7/X8 = 48, X9 = 64, X10/X11 = 192</li>
          <li>DB server hardware cost: count × $82,952/server × 75% (25% discount), depreciated over 4 years × 5</li>
          <li>DB server HW support: 12% of hardware cost annually × 5 years</li>
        </ul>

        <b>2. Storage (Storage Servers)</b>
        <ul>
          <li>High-Capacity (HC/disk) servers: count × $71,093 × 25% discount</li>
          <li>Extreme Flash (EF) servers: count × $87,323 × 25% discount</li>
          <li>Both depreciated over 4 years × 5, plus 12% annual HW support × 5</li>
          <li>Software support: HC servers × 12 cores × $10,000 × 65% discount × 22% support + EF servers × 8 cores × $20,000 × 65% discount × 22% support, × 5 years</li>
        </ul>

        <b>3. Database Support and Update Fees</b>
        <ul>
          <li>Same Oracle license support calculation as commodity x86 (22% of discounted license cost with 4% annual growth)</li>
          <li>Total vCPUs = cores_per_server × db_servers × 2</li>
        </ul>

        <b>4. Data Center (Racks)</b>
        <ul>
          <li>Per-rack cost derived from Exadata list pricing minus component costs, × 25% discount</li>
          <li>Depreciated over 4 years × 5, plus 12% annual HW support × 5</li>
        </ul>

        <b>5. Database Labor</b>
        <ul>
          <li>Same model as commodity x86 but uses "ExadataOnPremises" operate hours (3,198 hours/year reference)</li>
        </ul>

        <hr/>
        <h6>Target: AWS (RDS Oracle, Aurora, ODB@AWS, etc.)</h6>
        <p>Target costs use pre-calculated compute and storage prices from the DBCSI auto-right-sizer, then add networking, license support, labor, discounts, and business benefits.</p>

        <b>1. Compute</b>
        <ul>
          <li>The annual compute cost from the auto-right-sizer is divided by 12 to get a monthly value, then projected over 60 months with the configured annual growth rate</li>
          <li>If discounts are enabled, the discount multiplier is applied (not applicable to ODB@AWS targets)</li>
        </ul>

        <b>2. Storage</b>
        <ul>
          <li>Same projection method as compute: annual ÷ 12, then 60-month growth projection</li>
          <li>Discount multiplier applied if enabled (not applicable to ODB@AWS targets)</li>
        </ul>

        <b>3. Networking</b>
        <ul>
          <li><b>Egress</b>: 0.05% of storage_gb × 730 hours/month = estimated monthly egress GB, × $0.020/GB, projected over 60 months with annual growth</li>
          <li>Discount multiplier applied if enabled</li>
        </ul>

        <b>4. Database Support and Update Fees</b>
        <ul>
          <li><b>RDS Oracle EE (BYOL)</b>: Oracle support calculated using target_vcpus with core factor of 1.0 (authorized cloud licensing policy). EE products: EE + Diagnostic + Tuning + Partitioning (no RAC). Doubles for Multi-AZ deployments.</li>
          <li><b>RDS Oracle SE2 (License Included)</b>: $0 support (included in compute price)</li>
          <li><b>Aurora PostgreSQL/MySQL, RDS PostgreSQL/MySQL</b>: $0 (no Oracle/SQL license)</li>
          <li><b>ODB@AWS (BYOL)</b>: Oracle support with 0.5 core factor</li>
          <li><b>ODB@AWS (License Included)</b>: $0 support (included in compute price)</li>
        </ul>

        <b>5. Database Labor</b>
        <ul>
          <li>Uses "RDS" platform operate hours (1,873 hours/year reference) for RDS/Aurora targets</li>
          <li>Uses "ExadataCloud" platform operate hours (2,343 hours/year reference) for ODB@AWS targets</li>
          <li>Same scaling formula: hours × (labor_vcpus ÷ 576) × $130/hour × 5 years with 4% growth</li>
        </ul>

        <b>6. Managed Database Business Benefits</b>
        <ul>
          <li>A user-configurable dollar amount subtracted from the target total (default: $5,000/year × 5 years × number of databases)</li>
          <li>Based on <a href="https://d1.awsstatic.com/product-marketing/RDS/IDC%20Business%20Value%20of%20Amazon%20RDS%20Whitepaper%202024.pdf" target="_blank">IDC study</a> reporting average benefit of $19,860 per database per year</li>
        </ul>

        <b>7. Discounts</b>
        <ul>
          <li>If enabled, a cross-service PPA/EDP discount percentage is applied to compute, storage, and networking costs</li>
          <li>Not applied to ODB@AWS targets, license support, labor, or business benefits</li>
        </ul>

        <hr/>
        <h6>Labor Hours Reference (Annual Operate Hours per 576-vCPU Reference System)</h6>
        <b-table-simple small bordered style="font-size:12px" class="mt-2">
          <b-thead><b-tr>
            <b-th>Role</b-th><b-th class="text-right">On-Prem</b-th><b-th class="text-right">Exadata On-Prem</b-th><b-th class="text-right">Exadata Cloud</b-th><b-th class="text-right">EC2</b-th><b-th class="text-right">RDS/Aurora</b-th>
          </b-tr></b-thead>
          <b-tbody>
            <b-tr><b-td>Architecture</b-td><b-td class="text-right">296</b-td><b-td class="text-right">296</b-td><b-td class="text-right">280</b-td><b-td class="text-right">280</b-td><b-td class="text-right">280</b-td></b-tr>
            <b-tr><b-td>Database Administrator</b-td><b-td class="text-right">850</b-td><b-td class="text-right">656</b-td><b-td class="text-right">529</b-td><b-td class="text-right">713</b-td><b-td class="text-right">268</b-td></b-tr>
            <b-tr><b-td>Developer</b-td><b-td class="text-right">259</b-td><b-td class="text-right">196</b-td><b-td class="text-right">176</b-td><b-td class="text-right">176</b-td><b-td class="text-right">168</b-td></b-tr>
            <b-tr><b-td>Engineering</b-td><b-td class="text-right">660</b-td><b-td class="text-right">484</b-td><b-td class="text-right">386</b-td><b-td class="text-right">394</b-td><b-td class="text-right">354</b-td></b-tr>
            <b-tr><b-td>Infrastructure</b-td><b-td class="text-right">320</b-td><b-td class="text-right">296</b-td><b-td class="text-right">56</b-td><b-td class="text-right">44</b-td><b-td class="text-right">36</b-td></b-tr>
            <b-tr><b-td>Storage Administrator</b-td><b-td class="text-right">553</b-td><b-td class="text-right">298</b-td><b-td class="text-right">206</b-td><b-td class="text-right">178</b-td><b-td class="text-right">158</b-td></b-tr>
            <b-tr><b-td>System Administrator</b-td><b-td class="text-right">1,164</b-td><b-td class="text-right">972</b-td><b-td class="text-right">710</b-td><b-td class="text-right">667</b-td><b-td class="text-right">609</b-td></b-tr>
            <b-tr variant="secondary"><b-td><b>Total</b></b-td><b-td class="text-right"><b>4,102</b></b-td><b-td class="text-right"><b>3,198</b></b-td><b-td class="text-right"><b>2,343</b></b-td><b-td class="text-right"><b>2,452</b></b-td><b-td class="text-right"><b>1,873</b></b-td></b-tr>
          </b-tbody>
        </b-table-simple>

      </b-card-body>
    </b-collapse>
  </b-card>

      </b-card-body>
    </b-collapse>
  </b-card>

  <div v-if="!loading && !error && sourceResult" class="text-muted mt-3" style="font-size:12px">
    Thank you to @ravimat and @tjgeiger for help and support with the DBC and <a href="https://sample-solutions.com/" target="_blank">DBC Quick Calculator</a> logic.
  </div>
</div>
</template>

<script>
import { API } from "aws-amplify";
import { calculateDBC } from "./dbcsi_dbc.js";
import { convertToPricingCalculator, convertToPricingCalculatorAurora } from "./dbcsi_to_pricing_calculator.js";

// Input fields used to detect duplicate DBC results across scenarios
const RDS_KEYS = ['totalRDSComputecost', 'totalRDSStoragecost', 'totalRDSVCPU', 'totalRDSCount', 'rds_target_option_tag'];
const AURORA_KEYS = ['totalAuroraComputecost', 'totalAuroraStoragecost', 'totalAuroraVCPU', 'totalAuroraCount'];
const CONSOLIDATED_KEYS = ['totalRDSConsolidatedComputecost', 'totalRDSConsolidatedStoragecost', 'totalRDSConsolidatedVCPU', 'totalRDSConsolidatedCount'];
const ODB_KEYS = ['totalODBcost', 'totalODBdbservercost', 'totalODBstorageservercost', 'totalODBUsagecost', 'totalODBUsage', 'totalADBSCost', 'totalADBSEcpus', 'totalADBSEcpuCost', 'totalADBSStorageCost', 'odb_target_option_tag'];
const SOURCE_KEYS = ['totalSourceDBCount', 'totalSourceInstanceCount', 'totalSourceVCPU', 'totalSourceCores', 'totalSourceUsableDiskSpace', 'totalSourceDistinctServerCount', 'totalSourceDistinctServerCores', 'totalSourceDistinctServerVCPUs'];

function inputsMatch(a, b, keys) {
  return keys.every(k => a[k] === b[k]);
}

export default {
  props: {
    totals: { type: Array, required: true },
    scenarioIndex: { type: Number, default: null },
    batchItems: { type: Array, default: () => [] },
    batchId: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      error: null,
      sourceResult: null,
      exadataSourceResult: null,
      // scenarioResults[i] = { targetResult, auroraTargetResult, consolidatedTargetResult, odbTargetResult }
      scenarioResults: [],
      showAdvanced: false,
      showBenefits: false,
      adv: {
        total_vcpus: null,
        annual_growth_percentage: 0,
        business_benefit_amount: 5000,
        add_discounts: false,
        discount_percentage: 0,
        year1_service_credits: 0,
        year1_partner_cash_incentives: 0,
      },
      showCompute: false,
      showStorage: false,
      showDatacenterNetwork: false,
      showLicense: false,
      showLabor: false,
      copyLabel: '📋 Copy Table',
      showHelp: false,
      showIncentives: true,
      showIncentivesInput: false,
      showIncentivesRow: false,
      showSourceAssumptions: false,
      showDBC: true,
    };
  },
  computed: {
    multiScenario() {
      return this.scenarioIndex == null;
    },
    activeScenarios() {
      if (!this.multiScenario) return [this.scenarioIndex];
      return this.totals.map((_, i) => i);
    },
    sourcesDiffer() {
      if (!this.multiScenario) return false;
      const base = this.totals[this.activeScenarios[0]];
      return this.activeScenarios.slice(1).some(i => !inputsMatch(base, this.totals[i], SOURCE_KEYS));
    },
    hasExadata() {
      return this.activeScenarios.some(i => this.totals[i].sourceExadata);
    },
    hasIncentives() {
      return (this.adv.year1_service_credits || 0) + (this.adv.year1_partner_cash_incentives || 0) > 0;
    },
    exadataParams() {
      if (!this.hasExadata) return null;
      const base = this.totals[this.activeScenarios[0]];
      const serverCount = base.totalSourceDistinctServerCount;
      const db_servers = Math.max(2, serverCount);
      const coresPerServer = serverCount > 0 ? base.totalSourceDistinctServerCores / serverCount : 48;
      const exadata_version = coresPerServer < 48 ? 'X7' : coresPerServer < 64 ? 'X9' : 'X11';
      return {
        exadata_version,
        exadata_db_servers: db_servers,
        exadata_racks: Math.ceil(db_servers / 8),
        exadata_storage_servers: Math.max(3, Math.ceil(db_servers * 1.5)),
        exadata_storage_high_capacity: 0,
      };
    },
    targetColumns() {
      const cols = [];
      const seen = { rds: [], aurora: [], consolidated: [], odb: [] };

      for (const si of this.activeScenarios) {
        const total = this.totals[si];
        const sr = this.scenarioResults[si];
        if (!sr) continue;
        const prefix = this.multiScenario ? total.scenarioName + ' — ' : '';

        // RDS
        if (sr.targetResult && !seen.rds.some(prev => inputsMatch(total, this.totals[prev], RDS_KEYS))) {
          seen.rds.push(si);
          const rdsLabel = total.rds_target_option_tag === 'rds_oracle_se2_li' ? 'RDS-Oracle LI SE2' : 'RDS-Oracle BYOL EE';
          cols.push({ key: 'rds-' + si, label: prefix + rdsLabel, result: sr.targetResult });
        }

        // Aurora
        if (total.totalAuroracost > 0 && sr.auroraTargetResult && !seen.aurora.some(prev => inputsMatch(total, this.totals[prev], AURORA_KEYS))) {
          seen.aurora.push(si);
          cols.push({ key: 'aurora-' + si, label: prefix + 'Aurora PostgreSQL', result: sr.auroraTargetResult });
        }

        // Consolidated
        if (total.totalRDSConsolidatedcost > 0 && sr.consolidatedTargetResult && !seen.consolidated.some(prev => inputsMatch(total, this.totals[prev], CONSOLIDATED_KEYS))) {
          seen.consolidated.push(si);
          const rdsLabel = total.rds_target_option_tag === 'rds_oracle_se2_li' ? 'RDS-Oracle LI SE2' : 'RDS-Oracle BYOL EE';
          cols.push({ key: 'consolidated-' + si, label: prefix + 'Consolidated ' + rdsLabel, result: sr.consolidatedTargetResult });
        }

        // ODB
        const hasOdb = total.odb_target_option_tag && (total.odb_target_option_tag.startsWith('odb_adbs') ? total.totalADBSCost > 0 : total.totalODBcost > 0);
        if (hasOdb && sr.odbTargetResult && !seen.odb.some(prev => inputsMatch(total, this.totals[prev], ODB_KEYS))) {
          seen.odb.push(si);
          const tag = total.odb_target_option_tag || '';
          const odbLabels = {
            'odb_exacsd_byol': 'ODB@AWS ExaCS BYOL', 'odb_exacsd_li': 'ODB@AWS ExaCS LI',
            'odb_adbd_byol': 'ODB@AWS ADB-D BYOL', 'odb_adbd_li': 'ODB@AWS ADB-D LI',
            'odb_adbs_byol': 'ODB@AWS ADB-S BYOL', 'odb_adbs_li': 'ODB@AWS ADB-S LI',
          };
          cols.push({ key: 'odb-' + si, label: prefix + (odbLabels[tag] || 'ODB@AWS'), result: sr.odbTargetResult });
        }
      }
      return cols;
    },
  },
  mounted() {
    const base = this.totals[this.activeScenarios[0]];
    this.adv.total_vcpus = base.totalSourceDistinctServerVCPUs;
    this.adv.business_benefit_amount = 5000 * base.totalRDSCount;
    this.fetchDBC();
  },
  methods: {
    parseCurrency(val) {
      if (typeof val === 'number') return val;
      var n = parseFloat(String(val).replace(/[$,\s]/g, ''));
      return isNaN(n) ? 0 : n;
    },
    fmt(val) {
      return Math.round(val).toLocaleString("en-US");
    },
    breakoutLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b5yr\b/, '5-Year').replace(/\bdc\b/i, 'DC').replace(/\bOS\b/, 'OS').replace(/\b\w/g, c => c.toUpperCase());
    },
    fmtVal(val, key) {
      if (typeof val !== 'number') return val;
      if (key === 'labor_hourly_rate') return '$ ' + this.fmt(val);
      if (key && key.startsWith('labor_')) return val.toLocaleString("en-US");
      return val >= 1000 ? '$ ' + this.fmt(val) : val;
    },
    mergedBreakoutKeys(breakoutField) {
      const keys = new Set();
      const all = [this.sourceResult, this.exadataSourceResult];
      this.scenarioResults.forEach(sr => {
        if (sr) all.push(sr.targetResult, sr.auroraTargetResult, sr.consolidatedTargetResult, sr.odbTargetResult);
      });
      all.forEach(r => { if (r && r[breakoutField]) Object.keys(r[breakoutField]).forEach(k => keys.add(k)); });
      return Array.from(keys);
    },
    colSavings(col) {
      const base = this.exadataSourceResult || this.sourceResult;
      if (!base || !col.result) return 0;
      return base.total_5yr_cost - this.colTotal(col);
    },
    colSavingsPercent(col) {
      const base = this.exadataSourceResult || this.sourceResult;
      if (!base || base.total_5yr_cost === 0) return 0;
      return Math.round((this.colSavings(col) / base.total_5yr_cost) * 100);
    },
    colIsIncentiveEligible(col) {
      return col.key.startsWith('rds-') || col.key.startsWith('aurora-') || col.key.startsWith('consolidated-');
    },
    colIncentives(col) {
      if (!this.colIsIncentiveEligible(col)) return 0;
      return (this.adv.year1_service_credits || 0) + (this.adv.year1_partner_cash_incentives || 0);
    },
    colTotal(col) {
      return (col.result.total_5yr_cost || 0) - this.colIncentives(col);
    },
    copyTable() {
      const el = this.$refs.dbcTable.$el || this.$refs.dbcTable;
      const html = el.outerHTML;
      const plain = el.innerText;
      navigator.clipboard.write([new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      })]).then(() => {
        this.copyLabel = '✓ Copied!';
        setTimeout(() => { this.copyLabel = '📋 Copy Table'; }, 2000);
      }).catch(() => {
        this.copyLabel = '✗ Failed';
        setTimeout(() => { this.copyLabel = '📋 Copy Table'; }, 2000);
      });
    },
    scenarioHasAurora(scenarioIdx) {
      const scenarioName = this.totals[scenarioIdx].scenarioName;
      return this.batchItems.some(i => i.scenarioName === scenarioName && i.targetconfig_aurora && i.targetconfig_aurora.shape && i.targetconfig_aurora.shape !== 'N/A');
    },
    downloadPricingJSON(scenarioIdx, type) {
      const scenarioName = this.totals[scenarioIdx].scenarioName;
      const items = this.batchItems.filter(i => i.scenarioName === scenarioName);
      const sanitizedScenarioName = scenarioName.replace(/[^a-zA-Z0-9_-]/g, '_');
      let result, filename;
      if (type === 'aurora') {
        result = convertToPricingCalculatorAurora(items, 'DBCSI ' + scenarioName + ' - Aurora Estimate');
        filename = `dbcsi_pricing_calculator_aurora_${this.batchId}_${sanitizedScenarioName}.json`;
      } else {
        result = convertToPricingCalculator(items, 'DBCSI ' + scenarioName + ' - RDS Estimate');
        filename = `dbcsi_pricing_calculator_rds_${this.batchId}_${sanitizedScenarioName}.json`;
      }
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    async fetchDBC() {
      // Clean currency fields in case user pasted $1,500 and clicked Recalculate without blurring
      this.adv.year1_service_credits = this.parseCurrency(this.adv.year1_service_credits);
      this.adv.year1_partner_cash_incentives = this.parseCurrency(this.adv.year1_partner_cash_incentives);
      this.loading = true;
      this.error = null;
      this.scenarioResults = [];
      this.exadataSourceResult = null;

      try {
        // Source from first active scenario
        const baseTotal = this.totals[this.activeScenarios[0]];
        const baseResults = await calculateDBC(API, baseTotal, this.adv, this.exadataParams);
        if (baseResults.error) { this.error = baseResults.error; this.loading = false; return; }
        this.sourceResult = baseResults.sourceResult;
        this.exadataSourceResult = baseResults.exadataSourceResult;
        this.scenarioResults[this.activeScenarios[0]] = baseResults;

        // Remaining scenarios in parallel
        if (this.multiScenario && this.activeScenarios.length > 1) {
          const rest = this.activeScenarios.slice(1);
          const promises = rest.map(i => calculateDBC(API, this.totals[i], this.adv));
          const restResults = await Promise.all(promises);
          rest.forEach((i, idx) => {
            if (restResults[idx].error) this.error = this.error || restResults[idx].error;
            this.scenarioResults[i] = restResults[idx];
          });
          // Force reactivity since we're setting by index
          this.scenarioResults = [...this.scenarioResults];
        }

        if (!this.error) {
          const cleanResults = this.scenarioResults.map(r => r ? (({ error, ...rest }) => rest)(r) : r);
          await API.put("dbcsiAPI", "/dbcsi/features", {
            body: { dbc: 'Y', sourceResult: this.sourceResult, exadataSourceResult: this.exadataSourceResult, scenarioResults: cleanResults }
          });
        }
      } catch (e) {
        this.error = "Error calculating business case: " + (e.message || e);
      }

      this.loading = false;
    },
  },
};
</script>
